import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowUpRight, PlayCircle, UserPlus, AlertCircle, MapPin, ClipboardCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PayrollPredictorCard from "@/components/PayrollPredictorCard";
import GetStarted from "@/components/GetStarted";
import BuddyInsights from "@/components/BuddyInsights";
import OfficeManagerFeed from "@/components/OfficeManagerFeed";
import { BridgeMandates } from "@/components/BridgeMandates";
import { FundingHealthBar } from "@/components/FundingHealthBar";
import SovereignStatus from "@/components/SovereignStatus";

const CHART_TICK_STYLE = { fontFamily: "IBM Plex Mono", fontSize: 10 };
const CHART_TOOLTIP_STYLE = { border: "2px solid #0A0A0A", borderRadius: 0, fontFamily: "IBM Plex Mono" };

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [approvalCounts, setApprovalCounts] = useState({ pending: 0 });

  // A Hide-My-Email Apple sign-up lands on a brand-new company, because a
  // relay address can never be matched to an existing account. Say so.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get("apple_relay")) return;
    toast.info(
      "You signed in with Apple's Hide My Email, so this is a new workspace. Already have a "
      + "Checkmate account? Sign in your usual way, then link Apple under Company Settings.",
      { duration: 12000 },
    );
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const loadDashboard = useCallback(async () => {
    if (user?.role === "employee") return;
    const [s, e, tf, ap] = await Promise.all([
      api.get("/reports/summary"),
      api.get("/employees"),
      api.get("/time-entries?flagged=true"),
      api.get("/time-entries/summary"),
    ]);
    setSummary(s.data); setEmployees(e.data); setFlagged(tf.data); setApprovalCounts(ap.data);
  }, [user]);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (user?.role === "employee") return <Navigate to="/app/portal" replace />;

  const s = summary;
  return (
    <div className="p-10">
      <FundingHealthBar />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-10">
        <div>
          <div className="overline mb-2">§ OVERVIEW</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Dashboard</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Link data-testid="run-payroll-cta" to="/app/payroll/new" className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
          Launch Wizard <ArrowUpRight className="w-4 h-4" />
        </Link>
        <Link data-testid="saved-drafts-cta" to="/app/payroll?tab=drafts" className="border-2 border-ink px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white transition-colors">
          Manage Drafts <ClipboardCheck className="w-4 h-4" />
        </Link>
        </div>
      </div>

      <div className="mb-8"><SovereignStatus user={user} /></div>

      <PayrollPredictorCard />

      <GetStarted />

      <OfficeManagerFeed scope="admin" />
      <BuddyInsights />
      <BridgeMandates />

      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 lg:col-span-4 hard-card hard-card-shadow p-8">
          <div className="overline mb-3">ACTIVE EMPLOYEES</div>
          <div className="font-mono font-black text-6xl" data-testid="stat-active-employees">{s?.active_employees ?? "—"}</div>
          <Link to="/app/employees" className="mt-6 inline-flex items-center gap-2 border-b-2 border-ink text-sm font-mono uppercase tracking-widest">Manage <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
        <div className="col-span-12 lg:col-span-4 hard-card p-8">
          <div className="overline mb-3">PAYROLL RUNS · YTD</div>
          <div className="font-mono font-black text-6xl" data-testid="stat-total-runs">{s?.total_runs ?? 0}</div>
          <Link to="/app/payroll" className="mt-6 inline-flex items-center gap-2 border-b-2 border-ink text-sm font-mono uppercase tracking-widest">View history</Link>
        </div>
        <div className="col-span-12 lg:col-span-4 hard-card p-8 bg-ink text-white">
          <div className="overline text-white/60 mb-3">YTD GROSS</div>
          <div className="font-mono font-black text-5xl break-all" data-testid="stat-ytd-gross">{fmtMoney(s?.ytd?.gross)}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-6 font-mono text-xs">
            <div className="text-white/60">Federal</div><div className="text-right">{fmtMoney(s?.ytd?.federal_tax)}</div>
            <div className="text-white/60">FICA</div><div className="text-right">{fmtMoney((s?.ytd?.social_security || 0) + (s?.ytd?.medicare || 0))}</div>
            <div className="text-white/60">State</div><div className="text-right">{fmtMoney(s?.ytd?.state_tax)}</div>
            <div className="text-white/60 border-t border-white/30 pt-1 mt-1">Net</div><div className="text-right border-t border-white/30 pt-1 mt-1">{fmtMoney(s?.ytd?.net_pay)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 hard-card p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="overline">RECENT PAYROLL — GROSS vs NET vs TAXES</div>
          </div>
          <div className="h-72 min-h-[288px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s?.monthly || []}>
                <CartesianGrid stroke="#0A0A0A" strokeDasharray="2 2" opacity={0.15} />
                <XAxis dataKey="label" tick={CHART_TICK_STYLE} stroke="#0A0A0A" />
                <YAxis tick={CHART_TICK_STYLE} stroke="#0A0A0A" />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Bar dataKey="gross" fill="#0A0A0A" />
                <Bar dataKey="taxes" fill="#E1301A" />
                <Bar dataKey="net" fill="#00A050" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 hard-card p-8">
          <div className="overline mb-4">ALERTS & QUICK ACTIONS</div>
          <div className="space-y-3">
            {approvalCounts.pending > 0 && (
              <Link to="/app/approvals" className="block border-2 border-warn bg-warn/10 p-3" data-testid="pending-approvals-alert">
                <div className="flex items-start gap-3">
                  <ClipboardCheck className="w-5 h-5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-heading font-black text-sm">{approvalCounts.pending} pending approval{approvalCounts.pending === 1 ? "" : "s"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Time entries awaiting your review before payroll.</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </Link>
            )}
            {flagged.length > 0 && (
              <Link to="/app/time-entries" className="block border-2 border-bad bg-bad/10 p-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 shrink-0 text-bad" />
                  <div className="flex-1">
                    <div className="font-heading font-black text-sm text-bad" data-testid="flagged-count">{flagged.length} flagged clock-in{flagged.length === 1 ? "" : "s"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Employees clocked in outside their assigned worksite radius.</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-bad" />
                </div>
              </Link>
            )}
            {employees.length === 0 && (
              <div className="border-2 border-warn bg-warn/10 p-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-heading font-black text-sm">No employees yet</div>
                  <div className="text-xs text-muted-foreground">Add your first employee to run payroll.</div>
                </div>
              </div>
            )}
            <Link to="/app/employees" className="flex items-center justify-between border-2 border-ink p-4 hover:bg-ink hover:text-white transition-colors">
              <span className="font-mono text-sm uppercase tracking-widest flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add Employee</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/app/payroll/new" className="flex items-center justify-between border-2 border-ink p-4 hover:bg-ink hover:text-white transition-colors">
              <span className="font-mono text-sm uppercase tracking-widest flex items-center gap-2"><PlayCircle className="w-4 h-4" /> New Payroll Run</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/app/company" className="flex items-center justify-between border-2 border-ink p-4 hover:bg-ink hover:text-white transition-colors">
              <span className="font-mono text-sm uppercase tracking-widest">Company Settings</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
